<template>
	<div
		ref="data_input_menu"
		class="data-input-menu-content-container display-flex flex-column"
		:class="{ 'reversed-layout': isBottomToTopLayout, 'wide-mode': !isPreviousStepsSelectionMode }"
	>
		<DataInputSearch
			v-on="$listeners"
			ref="data_input_search"
			class="data-input-menu-search-field"
			:class="{ 'mb-1': !isBottomToTopLayout }"
			:searchString.sync="searchString"
			:hasBackIcon="hasBackIcon"
			:hasClearIcon="showClearIcon"
			:hasRefreshButton="hasExternalSource"
			:isTableColumnInput="isTableColumnInput"
			:isBottomToTopLayout="isBottomToTopLayout"
			:isShowingEmojiPicker="isShowingEmojiPicker"
			:showEmojiPickerButton="showEmojiPickerButton"
			:allowPreviousStepsSelection="allowPreviousStepsSelection"
			:isPreviousStepsSelectionMode="isPreviousStepsSelectionMode"
			:loading="loading"
			:disableMappedData="localDisableMappedData"
			@toggle-source-content="toggleSourceContent"
			@close-emoji-picker-mode="closeEmojiPickerMode"
			@toggle-emoji-picker-mode="toggleEmojiPickerMode"
			@on-blur="passEventToContentSwitcher('on-blur')"
			@on-enter="passEventToContentSwitcher('on-enter')"
			@on-arrow-up="passEventToContentSwitcher('on-arrow-up')"
			@on-arrow-left="passEventToContentSwitcher('on-arrow-left')"
			@on-arrow-down="passEventToContentSwitcher('on-arrow-down')"
			@on-arrow-right="passEventToContentSwitcher('on-arrow-right')"
			@un-expand-item="passEventToContentSwitcher('un-expand-item')"
		/>
		<InsertDataMenuContent
			v-on="$listeners"
			ref="menu_content_switcher"
			:isLoading="loading"
			:dataInputParam="param"
			:localValue="localValue"
			:isTableOnly="isTableOnly"
			:fieldParams="fieldParams"
			:hasMoreData="hasMoreData"
			:searchString="searchString"
			:errorMessage="errorMessage"
			:listButtonText="listButtonText"
			:listButtonBillingFeatureId="listButtonBillingFeatureId"
			:expandedItemId="expandedItemId"
			:useWithFormulas="useWithFormulas"
			:dataSourcesItems="dataSourcesItems"
			:showItemButtonFunc="showItemButtonFunc"
			:isTableColumnInput="isTableColumnInput"
			:allowTableSelection="allowTableSelection"
			:isCurTabTableParams="isCurTabTableParams"
			:isShowingEmojiPicker="isShowingEmojiPicker"
			:processWorkflowItems="processWorkflowItems"
			:isInternalEntityParams="isInternalEntityParams"
			:selectedProcessWorkflowId="selectedProcessWorkflowId"
			:isSelectParamsSelectionMode="isSelectParamsSelectionMode"
			:isPreviousStepsSelectionMode="isPreviousStepsSelectionMode"
			:allowMultipleSelection="allowMultipleSelection"
			:preventTables="preventTables"
			:serviceSlug="serviceSlug"
			:actionKey="actionKey"
			:paramsMap="paramsMap"
			@process-workflow-selected="selectProcessWorkflow"
			@set-data-input-menu-item="setItemFromInputMenu"
			@item-clicked="updateLocalValueFromTable"
			@focus-on-search="focusOnSearchInput"
			@set-expanded-item-id="(v) => (expandedItemId = v)"
			@select-emoji="selectEmoji"
			@un-expand-table-column-options="toggleSourceContent"
			@close-menu="closeMenu"
		/>
	</div>
</template>

<script>
import isEqual from 'lodash/isEqual';

import DataInputMenuService from './data-input-menu-service';
import DataInputSearch from './helpers/DataInputSearch';
import ExtendedDataInputMenuMixin from './helpers/ExtendedDataInputMenuMixin';
import InsertDataMenuContent from '@/components/DataInput/DataInputMenu/InsertDataMenuContent';
import DataInputMenuItemBaseMixin from '@/components/DataInput/DataInputMenu/MenuLists/helpers/DataInputMenuItemBaseMixin';
import {
	SELECT_TAB_KEY,
	RE_CLAC_MENU_POSITION,
	EXTERNAL_SOURCE_TAB_KEY,
	PREVIOUS_STEP_TABLE_OUTPUT,
	DATA_INPUT_MENU_MAX_HEIGHT,
	SET_VALUE_FROM_TABLE_SOURCE,
	TABLE_ONLY_ALLOWED_VALUE_TYPES,
	DATA_INPUT_MENU_ALLOWED_VALUES,
} from '@/components/DataInput/DataInputMenu/helpers/data-input-menu.const';
import ParamsService from '@/utils/ParamsService';

export default {
	name: 'DataInputMenu',
	mixins: [ExtendedDataInputMenuMixin, DataInputMenuItemBaseMixin],
	components: {
		DataInputSearch,
		InsertDataMenuContent,
	},
	props: {
		tabItems: { type: Array },
		loading: { type: Boolean },
		hasMoreData: { type: Boolean },
		errorMessage: { type: String },
		showMenuFlag: { type: Boolean },
		listButtonText: { type: String },
		didOpenBySlash: { type: Boolean },
		showItemButtonFunc: { type: Function },
		isBottomToTopLayout: { type: Boolean },
		isInternalEntityParams: { type: Boolean },
		preventTables: { type: Boolean },
		listButtonBillingFeatureId: { type: String },
		fieldParams: { type: [Array, Object, String, Number] },
		disableMappedData: { type: Boolean, default: false },
		localValue: { type: [Array, Object, String, Number, Boolean] },
		valuesTypes: { type: Array, validator: DataInputMenuService.valueTypesValidator, default: () => [] },
		serviceSlug: { type: String },
		actionKey: { type: String },
		paramsMap: { type: Object },
	},
	computed: {
		showEmojiPickerButton() {
			return !this.isShowingEmojiPicker && !this.allowPreviousStepsSelection;
		},
		showClearIcon() {
			return this.searchString?.length > 0;
		},
		hasBackIcon() {
			return Boolean(this.expandedItemId) || this.isShowingEmojiPicker || Boolean(this.selectedProcessWorkflowId);
		},
		allowPreviousStepsSelection() {
			return (
				this.isTableColumnInput ||
				this.isCurTabTableParams ||
				this.tabItems.some((tab) => [SELECT_TAB_KEY, EXTERNAL_SOURCE_TAB_KEY].includes(tab.tab))
			);
		},
		hasExternalSource() {
			return this.tabItems?.some((tab) => tab.tab === EXTERNAL_SOURCE_TAB_KEY);
		},
		isSelectParamsSelectionMode() {
			return this.isCurTabSelectParams;
		},
		isTableColumnInput() {
			return this.tabItems.some((tab) => PREVIOUS_STEP_TABLE_OUTPUT === tab.tab);
		},
		isTableOnly() {
			return isEqual(this.valuesTypes, TABLE_ONLY_ALLOWED_VALUE_TYPES);
		},
		allowTableSelection() {
			return DataInputMenuService.valueTypesAreTable(this.valuesTypes);
		},
		useWithFormulas() {
			return this.valuesTypes.every(
				(valueType) => DATA_INPUT_MENU_ALLOWED_VALUES.TABLE !== valueType && DATA_INPUT_MENU_ALLOWED_VALUES.TABLE_COLUMN !== valueType
			);
		},
		localDisableMappedData() {
			return this.param?.disable_mapped_data || this.disableMappedData;
		},
	},
	methods: {
		selectEmoji(emoji) {
			this.setTextItem(emoji);
		},
		passEventToContentSwitcher(eventName) {
			this.$refs.menu_content_switcher.handleEventFromParent(eventName);
		},
		toggleEmojiPickerMode() {
			this.isShowingEmojiPicker = !this.isShowingEmojiPicker;
		},
		closeEmojiPickerMode() {
			this.isShowingEmojiPicker = false;
		},
		closeMenu() {
			this.$emit('close');
		},
		toggleSourceContent() {
			this.expandedItemId = null;
			this.isPreviousStepsSelectionMode = !this.isPreviousStepsSelectionMode;
			this.$emit('update:tab', this.isPreviousStepsSelectionMode ? 1 : 0);
		},
		updateLocalValueFromTable({ value }) {
			const valueToSet = ParamsService.buildKeyForTableColumn(value);
			this.$emit(SET_VALUE_FROM_TABLE_SOURCE, valueToSet);
		},
		focusOnSearchInput() {
			setTimeout(() => {
				this.$refs.data_input_search.focus();
			}, 150);
		},
		onKeyDown(e) {
			if (['Escape', 'Tab'].includes(e.key)) {
				this.closeMenu();
				this.removeOnKeyDownListener();
			}
		},
		removeOnKeyDownListener() {
			if (this.onKeyDownHandler) {
				window.removeEventListener('keydown', this.onKeyDownHandler, true);
				this.onKeyDownHandler = null;
			}
		},
		listenOnKeyDown() {
			this.removeOnKeyDownListener();
			this.onKeyDownHandler = (e) => this.onKeyDown(e);
			window.addEventListener('keydown', this.onKeyDownHandler, true);
		},
		resetStateToDefault() {
			this.expandedItemId = null;
			this.isShowingEmojiPicker = false;
			setTimeout(() => {
				this.$refs.data_input_search?.clearSearchString();
			}, 300);
		},
		reCalcMenuPosition() {
			setTimeout(() => {
				this.$emit(RE_CLAC_MENU_POSITION);
			}, 10);
		},
		onMenuOpen() {
			if (this.didOpenBySlash) {
				this.focusOnSearchInput();
			}
			this.listenOnKeyDown();
			setTimeout(() => {
				this.reCalcMenuPosition();
			}, 300);

			this.isPreviousStepsSelectionMode = ![SELECT_TAB_KEY, EXTERNAL_SOURCE_TAB_KEY].includes(this.curTabCode);
			if (this.isTableColumnInput) {
				// If the menu opens on table column input, change tab to be the table's columns view
				this.toggleSourceContent();
			}
		},
		selectProcessWorkflow(workflowId) {
			this.selectedProcessWorkflowId = workflowId;
		},
	},
	data: () => ({
		blinkTimeout: null,
		blinkMessage: false,
		expandedItemId: null,
		onKeyDownHandler: null,
		EXTERNAL_SOURCE_TAB_KEY,
		isShowingEmojiPicker: false,
		selectedProcessWorkflowId: null,
		isPreviousStepsSelectionMode: true,
		DATA_INPUT_MENU_MAX_HEIGHT: String(DATA_INPUT_MENU_MAX_HEIGHT) + 'px',
	}),
	updated() {
		this.listenOnKeyDown();
	},
	mounted() {
		this.$nextTick(() => {
			this.onMenuOpen();
		});
	},
	watch: {
		didOpenBySlash(newV) {
			if (newV) {
				this.focusOnSearchInput();
			}
		},
		curTabCode() {
			this.reCalcMenuPosition();
		},
		searchString() {
			this.reCalcMenuPosition();
		},
		hasBackIcon() {
			this.reCalcMenuPosition();
			// has back icon changes every 'navigation' in-to and out-of sub menu, which requiers a recalaculation of menu position
		},
		showMenuFlag(newV) {
			newV
				? this.onMenuOpen()
				: setTimeout(() => {
						this.resetStateToDefault();
				  }, 200);
		},
	},
};
</script>

<style scoped lang="scss">
.reversed-layout {
	flex-direction: column-reverse !important;
}
.data-input-menu-content-container {
	width: 370px;
	overflow: hidden;
	background: var(--bg-color);
	&.wide-mode {
		width: 440px;
	}

	.content-container {
		overflow-y: auto;
		max-height: var(DATA_INPUT_MENU_MAX_HEIGHT);
	}
}
</style>
